import GUI from 'lil-gui';
import Stats from 'stats.js';
import type { Engine } from '@/core/Engine';
import { materialCount } from '@/art/materials';

/**
 * Ontwikkelaarsoverlay: frametijd, draw calls, tri-count en cameraregelaars.
 *
 * De budgetten uit docs/ARCHITECTURE.md staan hier zichtbaar naast de gemeten waarden, zodat
 * je meteen ziet wanneer een nieuw stuk stad eroverheen gaat in plaats van pas als het
 * traag aanvoelt.
 */

export interface DebugReadout {
  drawCalls: number;
  triangles: number;
  materials: number;
  lights: number;
  obstacles: number;
  chunks: number;
  traffic: number;
}

export interface DebugOverlayOptions {
  readonly onCameraZoomOverride?: (viewHeight: number) => void;
}

export class DebugOverlay {
  readonly #stats = new Stats();
  readonly #gui: GUI;
  readonly #readout: DebugReadout = {
    drawCalls: 0,
    triangles: 0,
    materials: 0,
    lights: 0,
    obstacles: 0,
    chunks: 0,
    traffic: 0,
  };

  #frame = 0;

  constructor(
    private readonly engine: Engine,
    private readonly lightCount: () => number,
    private readonly obstacleCount: () => number,
    private readonly worldStats: () => { loadedChunkIds: readonly string[]; activeTraffic: number } = () => ({
      loadedChunkIds: [],
      activeTraffic: 0,
    }),
    private readonly options: DebugOverlayOptions = {},
  ) {
    this.#stats.showPanel(0);
    Object.assign(this.#stats.dom.style, { left: 'auto', right: '0px', top: '0px' });
    document.body.appendChild(this.#stats.dom);

    this.#gui = new GUI({ title: 'New Amsterdam — debug' });
    this.#gui.close();

    const camera = this.#gui.addFolder('Camera');
    camera
      .add({ zoom: engine.isoCamera.viewHeight }, 'zoom', 6, 60, 1)
      .name('zichthoogte (m)')
      .onChange((value: number) => {
        engine.isoCamera.viewHeight = value;
        this.options.onCameraZoomOverride?.(value);
      });
    camera
      .add(
        {
          assetReview: () => {
            engine.isoCamera.setCharacterPixelHeight(200, engine.viewportHeight);
            this.options.onCameraZoomOverride?.(engine.isoCamera.viewHeight);
          },
        },
        'assetReview',
      )
      .name('asset-review (200 px)');

    const budget = this.#gui.addFolder('Budget');
    budget.add(this.#readout, 'drawCalls').name('draw calls (< 300)').listen().disable();
    budget.add(this.#readout, 'triangles').name('driehoeken').listen().disable();
    budget.add(this.#readout, 'materials').name('materialen').listen().disable();
    budget.add(this.#readout, 'lights').name('actieve lampen (< 12)').listen().disable();
    budget.add(this.#readout, 'obstacles').name('collision-blokken').listen().disable();
    budget.add(this.#readout, 'chunks').name('geladen chunks').listen().disable();
    budget.add(this.#readout, 'traffic').name('chunk-verkeer').listen().disable();
    budget.open();
  }

  beginFrame(): void {
    this.#stats.begin();
  }

  endFrame(): void {
    this.#stats.end();

    // De teller hoeft niet elke frame bij: 6 Hz is genoeg om afwijkingen te zien en
    // voorkomt dat de GUI zelf een meetbare kostenpost wordt.
    if (++this.#frame % 10 !== 0) return;

    const stats = this.engine.stats();
    this.#readout.drawCalls = stats.drawCalls;
    this.#readout.triangles = stats.triangles;
    this.#readout.materials = materialCount();
    this.#readout.lights = this.lightCount();
    this.#readout.obstacles = this.obstacleCount();
    const world = this.worldStats();
    this.#readout.chunks = world.loadedChunkIds.length;
    this.#readout.traffic = world.activeTraffic;
  }

  dispose(): void {
    this.#gui.destroy();
    this.#stats.dom.remove();
  }
}
